// image-processor.js - OpenCV 기반 이미지 후처리 파이프라인

import { isOpenCVReady } from './opencv-loader.js';

/**
 * 파일 크기 포맷
 * @param {number} bytes
 * @returns {string}
 */
export function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

/**
 * Data URL에서 Blob 크기 추정
 * @param {string} dataURL
 * @returns {number}
 */
function estimateBlobSize(dataURL) {
  // data:image/jpeg;base64, 형식에서 base64 부분만 추출
  const base64 = dataURL.split(',')[1];
  if (!base64) return 0;
  // Base64는 원본 대비 약 4/3 크기
  return Math.round(base64.length * 0.75);
}

/**
 * 이미지 Data URL을 HTMLImageElement로 로드
 * @param {string} dataURL
 * @returns {Promise<HTMLImageElement>}
 */
function loadImage(dataURL) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = dataURL;
  });
}

/**
 * Canvas를 Data URL로 변환 (형식/품질 지정)
 * @param {HTMLCanvasElement} canvas
 * @param {'png' | 'jpeg'} format
 * @param {number} quality - JPEG 품질 (0-1)
 * @returns {string}
 */
function canvasToDataURL(canvas, format, quality = 0.85) {
  return canvas.toDataURL(`image/${format}`, quality);
}

/**
 * 흑백 처리 (개선된 파이프라인)
 *
 * 파이프라인: Grayscale → CLAHE → GaussianBlur → adaptiveThreshold → morphOpen → morphClose
 *
 * 텍스트 문서에 최적, 가장 작은 파일 크기
 * 파라미터 재조정 (2026-01-29): 텍스트 디테일 보존을 위해 완화
 *
 * @param {cv.Mat} src - 입력 이미지 (RGBA)
 * @returns {cv.Mat} - 흑백 이미지 (RGBA)
 */
function processBW(src) {
  let gray = null;
  let enhanced = null;
  let bw = null;
  let result = null;
  let openKernel = null;
  let closeKernel = null;
  let clahe = null;

  try {
    // 1. 그레이스케일 변환
    gray = new cv.Mat();
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);

    // 2. CLAHE로 로컬 대비 향상 (clipLimit=2.5으로 텍스트 선명도 강화)
    enhanced = new cv.Mat();
    try {
      clahe = new cv.CLAHE(2.5, new cv.Size(8, 8));
      clahe.apply(gray, enhanced);
    } catch (e) {
      // CLAHE 미지원 시 일반 히스토그램 평활화로 폴백
      console.warn('CLAHE not available, using equalizeHist fallback');
      cv.equalizeHist(gray, enhanced);
    }

    // 3. Gaussian Blur 제거 - 원본 디테일 최대 보존

    // 4. Adaptive Threshold
    // blockSize=15: 세밀한 로컬 영역
    // C=5: 최소한의 배경 제거로 곡선 디테일 보존
    bw = new cv.Mat();
    cv.adaptiveThreshold(
      enhanced, bw, 255,
      cv.ADAPTIVE_THRESH_GAUSSIAN_C,
      cv.THRESH_BINARY,
      15,
      5
    );

    // 5. Morphological Opening 제거 - 세밀한 곡선 보존

    // 6. Morphological Closing (문자 내 작은 구멍 제거)
    closeKernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(2, 2));
    cv.morphologyEx(bw, bw, cv.MORPH_CLOSE, closeKernel);

    // 7. RGBA로 변환 (Canvas 출력용)
    result = new cv.Mat();
    cv.cvtColor(bw, result, cv.COLOR_GRAY2RGBA);

    return result;
  } finally {
    if (gray) gray.delete();
    if (enhanced) enhanced.delete();
    if (bw) bw.delete();
    if (openKernel) openKernel.delete();
    if (closeKernel) closeKernel.delete();
    if (clahe) clahe.delete();
  }
}

/**
 * 그레이스케일 처리 (CLAHE 기반)
 * 도면, 다이어그램에 좋음
 *
 * CLAHE: 로컬 대비 향상으로 전역 평활화보다 자연스러운 결과
 *
 * @param {cv.Mat} src - 입력 이미지 (RGBA)
 * @returns {cv.Mat} - 그레이스케일 이미지 (RGBA)
 */
function processGray(src) {
  let gray = null;
  let enhanced = null;
  let result = null;
  let clahe = null;

  try {
    // 그레이스케일 변환
    gray = new cv.Mat();
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);

    // CLAHE로 로컬 대비 향상 (clipLimit=1.6으로 자연스러운 결과)
    enhanced = new cv.Mat();
    try {
      clahe = new cv.CLAHE(1.6, new cv.Size(8, 8));
      clahe.apply(gray, enhanced);
    } catch (e) {
      // CLAHE 미지원 시 일반 히스토그램 평활화로 폴백
      console.warn('CLAHE not available, using equalizeHist fallback');
      cv.equalizeHist(gray, enhanced);
    }

    // RGBA로 변환
    result = new cv.Mat();
    cv.cvtColor(enhanced, result, cv.COLOR_GRAY2RGBA);

    return result;
  } finally {
    if (gray) gray.delete();
    if (enhanced) enhanced.delete();
    if (clahe) clahe.delete();
  }
}

/**
 * 컬러 보정 (LAB + CLAHE 기반)
 * 컬러 문서, 사진에 최적
 *
 * LAB 색공간의 L 채널에 CLAHE 적용으로 자연스러운 대비 향상
 *
 * @param {cv.Mat} src - 입력 이미지 (RGBA)
 * @returns {cv.Mat} - 색보정된 이미지 (RGBA)
 */
function processColor(src) {
  let rgb = null;
  let lab = null;
  let channels = null;
  let result = null;
  let clahe = null;
  let enhancedL = null;

  try {
    // RGBA -> RGB (LAB 변환 전)
    rgb = new cv.Mat();
    cv.cvtColor(src, rgb, cv.COLOR_RGBA2RGB);

    // RGB -> LAB
    lab = new cv.Mat();
    cv.cvtColor(rgb, lab, cv.COLOR_RGB2Lab);

    // 채널 분리
    channels = new cv.MatVector();
    cv.split(lab, channels);

    // L 채널 (밝기)에 CLAHE 적용 (clipLimit=1.6으로 자연스러운 결과)
    const lChannel = channels.get(0);
    enhancedL = new cv.Mat();
    try {
      clahe = new cv.CLAHE(1.6, new cv.Size(8, 8));
      clahe.apply(lChannel, enhancedL);
      // 원본 L 채널을 enhanced로 교체
      enhancedL.copyTo(lChannel);
    } catch (e) {
      // CLAHE 미지원 시 단순 정규화로 폴백
      console.warn('CLAHE not available, using normalize fallback');
      cv.normalize(lChannel, lChannel, 0, 255, cv.NORM_MINMAX);
    }

    // 채널 병합
    cv.merge(channels, lab);

    // LAB -> RGB -> RGBA
    cv.cvtColor(lab, rgb, cv.COLOR_Lab2RGB);
    result = new cv.Mat();
    cv.cvtColor(rgb, result, cv.COLOR_RGB2RGBA);

    return result;
  } finally {
    if (rgb) rgb.delete();
    if (lab) lab.delete();
    if (enhancedL) enhancedL.delete();
    if (clahe) clahe.delete();
    if (channels) {
      for (let i = 0; i < channels.size(); i++) {
        channels.get(i).delete();
      }
      channels.delete();
    }
  }
}

/**
 * Mat을 Canvas에 렌더링 후 Data URL 반환
 * @param {cv.Mat} mat
 * @param {'png' | 'jpeg'} format
 * @param {number} quality
 * @returns {string}
 */
function matToDataURL(mat, format, quality) {
  const canvas = document.createElement('canvas');
  canvas.width = mat.cols;
  canvas.height = mat.rows;
  cv.imshow(canvas, mat);
  return canvasToDataURL(canvas, format, quality);
}

/**
 * 모든 처리 옵션 생성 (백그라운드 처리)
 *
 * @param {string} imageDataURL - 원근보정된 이미지
 * @param {(progress: number, step: string) => void} [onProgress] - 진행률 콜백
 * @returns {Promise<{
 *   bw: { dataURL: string, estimatedSize: number },
 *   gray: { dataURL: string, estimatedSize: number },
 *   color: { dataURL: string, estimatedSize: number }
 * }>}
 */
export async function processAllVariants(imageDataURL, onProgress = () => {}) {
  if (!isOpenCVReady()) {
    throw new Error('OpenCV not ready');
  }

  const img = await loadImage(imageDataURL);

  // Canvas에서 ImageData 추출
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

  // OpenCV Mat으로 변환
  let src = null;
  let bwMat = null;
  let grayMat = null;
  let colorMat = null;

  try {
    src = cv.matFromImageData(imageData);

    // 1. 흑백 처리
    onProgress(10, '흑백 처리 중...');
    bwMat = processBW(src);
    const bwDataURL = matToDataURL(bwMat, 'png', 1.0); // PNG: 이진 이미지에 JPEG 아티팩트 방지
    bwMat.delete();
    bwMat = null;

    // 2. 그레이스케일 처리
    onProgress(40, '그레이스케일 처리 중...');
    grayMat = processGray(src);
    const grayDataURL = matToDataURL(grayMat, 'jpeg', 0.88);
    grayMat.delete();
    grayMat = null;

    // 3. 컬러 처리
    onProgress(70, '컬러 보정 중...');
    colorMat = processColor(src);
    const colorDataURL = matToDataURL(colorMat, 'jpeg', 0.92);
    colorMat.delete();
    colorMat = null;

    onProgress(100, '완료');

    return {
      bw: {
        dataURL: bwDataURL,
        estimatedSize: estimateBlobSize(bwDataURL)
      },
      gray: {
        dataURL: grayDataURL,
        estimatedSize: estimateBlobSize(grayDataURL)
      },
      color: {
        dataURL: colorDataURL,
        estimatedSize: estimateBlobSize(colorDataURL)
      }
    };
  } finally {
    if (src) src.delete();
    if (bwMat) bwMat.delete();
    if (grayMat) grayMat.delete();
    if (colorMat) colorMat.delete();
  }
}

/**
 * 단일 필터 적용
 *
 * @param {string} imageDataURL - 입력 이미지
 * @param {'bw' | 'gray' | 'color'} filter - 적용할 필터
 * @returns {Promise<string>} - 처리된 이미지 Data URL
 */
export async function applyFilter(imageDataURL, filter) {
  if (!isOpenCVReady()) {
    // OpenCV 미준비 시 원본 반환
    return imageDataURL;
  }

  const img = await loadImage(imageDataURL);

  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

  let src = null;
  let result = null;

  try {
    src = cv.matFromImageData(imageData);

    switch (filter) {
      case 'bw':
        result = processBW(src);
        return matToDataURL(result, 'png', 1.0);
      case 'gray':
        result = processGray(src);
        return matToDataURL(result, 'jpeg', 0.88);
      case 'color':
        result = processColor(src);
        return matToDataURL(result, 'jpeg', 0.92);
      default:
        return imageDataURL;
    }
  } finally {
    if (src) src.delete();
    if (result) result.delete();
  }
}
